import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../store/authStore.jsx';
import { getImages } from '../api/images';
import { getFolders } from '../api/folders';
import { getCategories } from '../api/categories';
import UploadZone from '../components/images/UploadZone';
import ImageGrid  from '../components/images/ImageGrid';
import ExportModal from '../components/images/ExportModal';
import FolderImport from '../components/images/FolderImport';

export default function DashboardPage() {
  const { user }           = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial filter values from URL query params
  const [folderId,    setFolderIdState]    = useState(searchParams.get('folder_id')   || '');
  const [categoryId,  setCategoryIdState]  = useState(searchParams.get('category_id') || '');
  const [searchInput, setSearchInput]      = useState('');
  const [q,           setQ]                = useState('');
  const [sort,        setSort]             = useState('newest');
  const [page,        setPage]             = useState(1);
  const [exportImgs,       setExportImgs]       = useState(null);
  const [preparingExport,  setPreparingExport]  = useState(false);

  function setFolderId(v)   { setFolderIdState(v);   setPage(1); }
  function setCategoryId(v) { setCategoryIdState(v); setPage(1); }

  const { data: imagesData, isLoading, isFetching } = useQuery({
    queryKey: ['images', { folderId, categoryId, q, sort, page }],
    queryFn:  () => getImages({
      folder_id:   folderId   || undefined,
      category_id: categoryId || undefined,
      q:           q          || undefined,
      sort,
      page,
      limit: 40,
    }).then((r) => r.data),
    keepPreviousData: true,
  });

  const { data: folders    = [] } = useQuery({ queryKey: ['folders'],    queryFn: () => getFolders().then(r => r.data) });
  const { data: categories = [] } = useQuery({ queryKey: ['categories'], queryFn: () => getCategories().then(r => r.data) });

  const images = imagesData?.data ?? [];
  const meta   = imagesData?.meta ?? { total: 0, pages: 1 };

  const planLabel    = ['', 'Free', 'Pro', 'Team', 'Enterprise'][user?.plan_id] ?? `Plan #${user?.plan_id}`;
  const hasFilters   = folderId || categoryId || q;

  function handleSearch(e) {
    e.preventDefault();
    setQ(searchInput.trim());
    setPage(1);
  }

  function clearFilters() {
    setFolderIdState('');
    setCategoryIdState('');
    setSearchInput('');
    setQ('');
    setPage(1);
  }

  // Export every image in the current folder/category filter as a PDF (up to 100)
  async function exportFilterAsPdf() {
    setPreparingExport(true);
    try {
      const r = await getImages({
        folder_id:   folderId   || undefined,
        category_id: categoryId || undefined,
        q:           q          || undefined,
        page: 1,
        limit: 100,
      });
      const imgs = (r.data?.data ?? []).filter((i) => i.mime_type.startsWith('image/'));
      if (imgs.length) setExportImgs(imgs);
    } finally {
      setPreparingExport(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">
            Welcome back, {user?.display_name}
          </h1>
          <p className="text-gray-400 text-sm mt-0.5">{planLabel} plan</p>
        </div>
        <StorageWidget user={user} />
      </div>

      {/* Upload zone */}
      <UploadZone folderId={folderId || null} categoryId={categoryId || null} />

      {/* Bulk folder import — ideal on the phone: pick your Screenshots folder, only new files upload */}
      <div className="-mt-1">
        <FolderImport compact />
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-1.5">
          <input
            className="input text-sm w-44 sm:w-56"
            placeholder="Search images…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className="btn-secondary text-sm px-3">Search</button>
        </form>

        {/* Folder filter */}
        <select
          className="input w-auto text-sm"
          value={folderId}
          onChange={(e) => setFolderId(e.target.value)}
        >
          <option value="">All folders</option>
          {folders.map((f) => (
            <option key={f.id} value={f.id}>{f.name} ({f.image_count})</option>
          ))}
        </select>

        {/* Category filter */}
        <select
          className="input w-auto text-sm"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.image_count})</option>
          ))}
        </select>

        {/* Sort */}
        <select
          className="input w-auto text-sm"
          value={sort}
          onChange={(e) => { setSort(e.target.value); setPage(1); }}
          title="Screenshots are timestamp-named, so “Name Z→A” shows the newest capture first"
        >
          <option value="newest">Recently added</option>
          <option value="oldest">Oldest added</option>
          <option value="name_desc">Name Z→A (newest screenshots)</option>
          <option value="name_asc">Name A→Z</option>
        </select>

        {hasFilters && (
          <button className="btn-secondary text-xs px-3 py-1.5" onClick={clearFilters}>
            Clear
          </button>
        )}

        {(folderId || categoryId) && (
          <button
            className="btn-secondary text-xs px-3 py-1.5"
            onClick={exportFilterAsPdf}
            disabled={preparingExport}
            title="Export all images in this filter as a PDF (up to 100)"
          >
            {preparingExport ? 'Preparing…' : '↓ Export all as PDF'}
          </button>
        )}

        <span className="text-gray-500 text-sm ml-auto flex items-center gap-2">
          {isFetching && !isLoading && (
            <span className="w-3 h-3 border border-brand-500 border-t-transparent rounded-full animate-spin inline-block" />
          )}
          {meta.total} image{meta.total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : images.length === 0 ? (
        <div className="card p-12 flex flex-col items-center text-center gap-2">
          <p className="text-4xl">{hasFilters ? '🔍' : '📸'}</p>
          <p className="text-gray-300 font-medium">
            {hasFilters ? 'No images match your filters' : 'No images yet'}
          </p>
          <p className="text-gray-500 text-sm">
            {hasFilters ? 'Try clearing the filters.' : 'Drop an image above to get started.'}
          </p>
          {hasFilters && (
            <button className="btn-secondary text-sm mt-1" onClick={clearFilters}>Clear filters</button>
          )}
        </div>
      ) : (
        <ImageGrid images={images} />
      )}

      {/* Pagination */}
      {meta.pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            className="btn-secondary px-3 py-1.5 text-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>
          <span className="text-gray-400 text-sm">Page {page} of {meta.pages}</span>
          <button
            className="btn-secondary px-3 py-1.5 text-sm"
            disabled={page >= meta.pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {exportImgs && (
        <ExportModal images={exportImgs} onClose={() => setExportImgs(null)} />
      )}
    </div>
  );
}

const PLAN_LIMITS = { 1: 500, 2: 20480, 3: 102400, 4: -1 };

function StorageWidget({ user }) {
  const limit = PLAN_LIMITS[user?.plan_id] ?? 500;
  const used  = user?.storage_used_mb || 0;
  const pct   = limit === -1 ? 100 : Math.min(100, (used / limit) * 100);
  const color = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-yellow-500' : 'bg-brand-500';
  const label = limit === -1 ? '∞' : (limit >= 1024 ? `${limit / 1024} GB` : `${limit} MB`);

  return (
    <div className="card px-4 py-3 min-w-[180px]">
      <div className="flex justify-between text-xs text-gray-400 mb-1.5">
        <span>Storage</span>
        <span>{used.toFixed(1)} / {label}</span>
      </div>
      <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
